class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this.input && this.input.form) {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    if (this.input) {
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__button')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i < allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex <= 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    if (activeElement.id) {
      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const searchUrl = `/search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection,page,article&resources[limit]=8&resources[limit_scope]=each&resources[options][unavailable_products]=last&resources[options][fields]=title,body,product_type,variants.title,vendor,tag`;

    fetch(searchUrl, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.json();
      })
      .then((json) => {
        const resultsMarkup = this.buildResultsHTML(json, searchTerm);
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
      });
  }

  formatPrice(rawPrice) {
    if (rawPrice === null || rawPrice === undefined || rawPrice === '') return '';
    const num = typeof rawPrice === 'string' ? parseFloat(rawPrice) : Number(rawPrice);
    if (isNaN(num)) return '';
    return '$' + num.toFixed(2);
  }

  escapeHTML(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  buildResultsHTML(json, searchTerm) {
    const results = json.resources?.results || {};
    const products = (results.products || []).filter((product) => {
      return ![8097871462518, 8097874673782].includes(Number(product.id)) &&
        !/\/(premium-emt-trauma-shears-7-5-free-gift|slishman-pressure-wrap-free-gift)(?:[?/#]|$)/.test(product.url || '') &&
        !['premium-emt-trauma-shears-7-5-free-gift', 'slishman-pressure-wrap-free-gift'].includes(product.handle);
    });
    const collections = results.collections || [];
    const pages = results.pages || [];
    const articles = results.articles || [];
    const totalCount = products.length + collections.length + pages.length + articles.length;

    const safeSearchTerm = this.escapeHTML(searchTerm);

    if (totalCount === 0) {
      return '<div class="predictive-search__empty">No results found for &ldquo;' + safeSearchTerm + '&rdquo;<div class="predictive-search__empty-hint">Try checking your spelling or searching for a broader term like &ldquo;IFAK&rdquo;, &ldquo;tourniquet&rdquo;, or &ldquo;gauze&rdquo;.</div></div><span data-predictive-search-live-region-count-value>No results found</span>';
    }

    const hasNavResults = collections.length > 0 || pages.length > 0 || articles.length > 0;
    const hasProducts = products.length > 0;

    let html = '<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper' + (hasNavResults && hasProducts ? ' predictive-search--split' : '') + '">';

    // Left Column: Collections, Articles/Protocols, Pages
    if (hasNavResults) {
      html += '<div class="predictive-search__nav-column">';

      // Collections Section
      if (collections.length > 0) {
        html += '<div class="predictive-search__result-group predictive-search__result-group--collections">';
        html += '<div class="predictive-search__heading-wrap"><span class="predictive-search__heading-icon"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg></span><h3 class="predictive-search__heading">Categories &amp; Collections</h3><span class="predictive-search__heading-badge">' + collections.length + '</span></div>';
        html += '<ul class="predictive-search__results-list" role="listbox" aria-label="Collections">';
        collections.forEach((collection) => {
          html += '<li class="predictive-search__list-item" role="option" aria-selected="false">';
          html += '<a href="' + collection.url + '" class="predictive-search__item predictive-search__item--collection" tabindex="-1">';
          html += '<span class="predictive-search__item-nav-bullet"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"></polyline></svg></span>';
          html += '<span class="predictive-search__item-title">' + this.escapeHTML(collection.title) + '</span>';
          html += '</a></li>';
        });
        html += '</ul></div>';
      }

      // Clinical Articles & Protocols Section
      if (articles.length > 0) {
        html += '<div class="predictive-search__result-group predictive-search__result-group--articles">';
        html += '<div class="predictive-search__heading-wrap"><span class="predictive-search__heading-icon"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg></span><h3 class="predictive-search__heading">Clinical Doctrine &amp; Articles</h3><span class="predictive-search__heading-badge">' + articles.length + '</span></div>';
        html += '<ul class="predictive-search__results-list" role="listbox" aria-label="Articles">';
        articles.forEach((article) => {
          html += '<li class="predictive-search__list-item" role="option" aria-selected="false">';
          html += '<a href="' + article.url + '" class="predictive-search__item predictive-search__item--article" tabindex="-1">';
          html += '<div class="predictive-search__item-nav-meta">';
          html += '<span class="predictive-search__item-title">' + this.escapeHTML(article.title) + '</span>';
          if (article.tags && article.tags.length > 0) {
            const firstTag = article.tags[0];
            html += '<span class="predictive-search__item-tag">' + this.escapeHTML(firstTag) + '</span>';
          }
          html += '</div>';
          html += '<span class="predictive-search__item-nav-arrow">&rarr;</span>';
          html += '</a></li>';
        });
        html += '</ul></div>';
      }

      // Pages & Resources Section
      if (pages.length > 0) {
        html += '<div class="predictive-search__result-group predictive-search__result-group--pages">';
        html += '<div class="predictive-search__heading-wrap"><span class="predictive-search__heading-icon"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg></span><h3 class="predictive-search__heading">Resources &amp; Hubs</h3><span class="predictive-search__heading-badge">' + pages.length + '</span></div>';
        html += '<ul class="predictive-search__results-list" role="listbox" aria-label="Pages">';
        pages.forEach((page) => {
          html += '<li class="predictive-search__list-item" role="option" aria-selected="false">';
          html += '<a href="' + page.url + '" class="predictive-search__item predictive-search__item--page" tabindex="-1">';
          html += '<span class="predictive-search__item-nav-bullet"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"></polyline></svg></span>';
          html += '<span class="predictive-search__item-title">' + this.escapeHTML(page.title) + '</span>';
          html += '</a></li>';
        });
        html += '</ul></div>';
      }

      html += '</div>'; // End nav-column
    }

    // Right Column: Products (Grid)
    if (hasProducts) {
      html += '<div class="predictive-search__products-column">';
      html += '<div class="predictive-search__heading-wrap"><span class="predictive-search__heading-icon"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg></span><h3 class="predictive-search__heading">Products</h3><span class="predictive-search__heading-badge">' + products.length + '</span></div>';
      html += '<ul class="predictive-search__products-grid" role="listbox" aria-label="Products">';

      products.forEach((product) => {
        const rawImgUrl = product.featured_image?.url || product.image;
        let imgHtml = '';
        if (rawImgUrl) {
          const sep = rawImgUrl.indexOf('?') !== -1 ? '&' : '?';
          const thumbUrl = rawImgUrl + sep + 'width=160&height=160&crop=center';
          const altText = this.escapeHTML(product.featured_image?.alt || product.title);
          imgHtml = '<div class="predictive-search__product-thumb-wrap"><img class="predictive-search__product-thumb" src="' + thumbUrl + '" alt="' + altText + '" width="70" height="70" loading="lazy"></div>';
        } else {
          imgHtml = '<div class="predictive-search__product-thumb-wrap predictive-search__product-thumb-placeholder"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9C8F61" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg></div>';
        }

        const priceNum = parseFloat(product.price);
        const compNum = parseFloat(product.compare_at_price_max || product.compare_at_price || 0);
        const isOnSale = !isNaN(compNum) && !isNaN(priceNum) && compNum > priceNum;

        let priceHtml = '';
        if (isOnSale) {
          priceHtml = '<div class="predictive-search__product-price predictive-search__product-price--sale"><span class="predictive-search__product-price-current">' + this.formatPrice(product.price) + '</span><s class="predictive-search__product-price-compare">' + this.formatPrice(compNum) + '</s></div>';
        } else if (!isNaN(priceNum) && priceNum > 0) {
          priceHtml = '<div class="predictive-search__product-price"><span class="predictive-search__product-price-current">' + this.formatPrice(product.price) + '</span></div>';
        }

        const vendorText = product.vendor ? '<span class="predictive-search__product-vendor">' + this.escapeHTML(product.vendor) + '</span>' : '';

        html += '<li class="predictive-search__product-card-item" role="option" aria-selected="false">';
        html += '<a href="' + product.url + '" class="predictive-search__product-card" tabindex="-1">';
        html += imgHtml;
        html += '<div class="predictive-search__product-info">';
        if (vendorText) html += vendorText;
        html += '<span class="predictive-search__product-title">' + this.escapeHTML(product.title) + '</span>';
        html += priceHtml;
        html += '</div>';
        html += '</a></li>';
      });

      html += '</ul></div>'; // End products-column
    }

    html += '</div>'; // End wrapper

    // Footer with Search for All Results button
    html += '<div class="predictive-search__footer">';
    html += '<button type="submit" class="predictive-search__button" data-predictive-search-search-for-text>';
    html += '<span>Search all results for <strong>&ldquo;' + safeSearchTerm + '&rdquo;</strong></span>';
    html += '<span class="predictive-search__button-count">(' + totalCount + '+ found) &rarr;</span>';
    html += '</button>';
    html += '</div>';

    html += '<span data-predictive-search-live-region-count-value>' + totalCount + ' results found</span>';

    return html;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const headerEl = this.closest('header') || document.querySelector('.fudge-y6fnr-site-header');
    this.resultsMaxHeight =
      window.innerHeight - (headerEl ? headerEl.getBoundingClientRect().bottom : 120);
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

if (!customElements.get('predictive-search')) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (!customElements.get('predictive-search')) {
        customElements.define('predictive-search', PredictiveSearch);
      }
    });
  } else {
    customElements.define('predictive-search', PredictiveSearch);
  }
}
